/**
 * SoleadLab 2.0 — FL-3a field-photo strip
 *
 * Horizontal thumbnail strip for an indication's properties.fieldPhotos,
 * shared by the Field update card (tech view, with own-photo remove) and
 * IndicationPreview (owner / view-link read-only). Thumbs and the full-screen
 * viewer use plain <img> tags with NO crossOrigin attribute so the sl_share /
 * sl_refresh cookie ships with the request — the same auth path as every
 * other indication photo (see handleGetAsset path 3/4).
 */

import { useState } from 'react';
import { createPortal } from 'react-dom';
import type { FieldPhotoEntry } from '../../stores/projectStore';
import { buildAssetUrl } from '../../services/mobileUploadApi';
import ZoomableImage from './ZoomableImage';
import './FieldPhotosStrip.css';

interface FieldPhotosStripProps {
  photos: FieldPhotoEntry[];
  /** When set, entries with this linkId get a Remove affordance. */
  canRemoveLinkId?: string | null;
  onRemove?: (photoId: string) => void;
  /** Disables the remove affordance during an in-flight write. */
  busy?: boolean;
}

function photoDateLabel(iso: string): string {
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleDateString();
}

/**
 * Defense in depth: only render entries whose key has the shape the attach
 * endpoint produces — under a site's `field/` prefix, a single path segment,
 * and a server-derived image extension (utils/uploadValidation.ts
 * MIME_EXTENSIONS, image half). Every entry SHOULD be server-stamped, but
 * this component fetches whatever `key` it is handed using the viewer's own
 * credentials, so a malformed one must never reach the network. The hole that
 * made that reachable (client-supplied fieldPhotos on create) is closed
 * server-side; this keeps the render path safe on its own terms.
 *
 * The filename is deliberately NOT pinned to the UUID the server happens to
 * generate: the security property is "cannot leave the field prefix" (no
 * slashes, no dots, no traversal), and over-fitting to today's id format
 * would silently blank real photos if that ever changes.
 */
const FIELD_PHOTO_KEY_RE =
  /^sites\/[A-Za-z0-9_-]+\/field\/[A-Za-z0-9_-]+\.(jpg|png|webp|heic|heif)$/;

export function isRenderableFieldPhoto(entry: FieldPhotoEntry): boolean {
  return typeof entry.key === 'string' && FIELD_PHOTO_KEY_RE.test(entry.key);
}

export default function FieldPhotosStrip({
  photos,
  canRemoveLinkId,
  onRemove,
  busy,
}: FieldPhotosStripProps) {
  const [viewer, setViewer] = useState<FieldPhotoEntry | null>(null);
  const [failed, setFailed] = useState<Set<string>>(new Set());

  const renderable = photos.filter(isRenderableFieldPhoto);
  if (renderable.length === 0) return null;

  const markFailed = (id: string) =>
    setFailed((prev) => new Set(prev).add(id));

  const canRemove = (entry: FieldPhotoEntry) =>
    !!canRemoveLinkId && !!onRemove && entry.linkId === canRemoveLinkId;

  return (
    <>
      <div className="field-photos" data-testid="field-photos-strip">
        {renderable.map((entry) => (
          <button
            key={entry.id}
            type="button"
            className="field-photos__thumb"
            onClick={() => setViewer(entry)}
            aria-label={`Field photo by ${entry.actor}`}
            data-testid={`field-photo-thumb-${entry.id}`}
          >
            {failed.has(entry.id) ? (
              <span className="field-photos__broken" aria-hidden>🖼</span>
            ) : (
              <img
                src={buildAssetUrl(entry.key)}
                alt=""
                loading="lazy"
                onError={() => markFailed(entry.id)}
              />
            )}
          </button>
        ))}
      </div>

      {/* Portal: both mount points (field card, marker preview) live inside
          CSS-transformed ancestors, which turn position:fixed into
          position-relative-to-the-card — the viewer would render trapped in a
          380px box. document.body is transform-free. */}
      {viewer && createPortal(
        <div
          className="field-photos__viewer"
          role="dialog"
          aria-label="Field photo"
          data-testid="field-photo-viewer"
        >
          <div className="field-photos__viewer-body">
            <ZoomableImage
              src={buildAssetUrl(viewer.key)}
              alt={`Field photo by ${viewer.actor}`}
              className="field-photos__viewer-img"
            />
          </div>
          <div className="field-photos__viewer-bar">
            <span className="field-photos__viewer-caption">
              {viewer.actor}
              {photoDateLabel(viewer.at) ? ` · ${photoDateLabel(viewer.at)}` : ''}
            </span>
            {canRemove(viewer) && (
              <button
                type="button"
                className="field-photos__viewer-remove"
                disabled={busy}
                onClick={() => {
                  if (!window.confirm('Remove this photo?')) return;
                  onRemove!(viewer.id);
                  setViewer(null);
                }}
                data-testid="field-photo-remove"
              >
                Remove
              </button>
            )}
            <button
              type="button"
              className="field-photos__viewer-close"
              onClick={() => setViewer(null)}
              data-testid="field-photo-viewer-close"
            >
              Close
            </button>
          </div>
        </div>,
        document.body,
      )}
    </>
  );
}
